// What the desk sees of the window-1 card findings (PMS-LEDGER-L0B-BRIEF §3.5).
// Pure, so it is tested alone: no React, no port.

import type { CardFinding, CardFindingClass } from '@lemontaigu/pms-core';
import { fmtAmount } from './logic.js';

/** The order the classes read in, and their titles. Wrong tender and duplicates
 *  first: they are the ones the desk can fix in Vega in a minute. */
export const CLASS_ORDER: readonly CardFindingClass[] = ['A', 'H', 'N', 'C', 'B', 'E'];

export const CLASS_TITLE_FR: Record<CardFindingClass, string> = {
  A: 'Mode de règlement à corriger (carte)',
  H: 'Carte ou espèces ?',
  N: 'Arrhes réécrites après leur déduction',
  C: 'Paiement saisi deux fois',
  B: 'Saisi dans Vega, jamais débité',
  E: 'Débité, jamais saisi',
};

/** Findings the desk can still act on. `awaiting_remise` is not a finding: it
 *  cannot be judged yet. */
export const isActionable = (f: CardFinding): boolean => f.state === 'lead';

export const leadCount = (findings: readonly CardFinding[]): number => findings.filter(isActionable).length;

export function groupByClass(findings: readonly CardFinding[]): { cls: CardFindingClass; items: CardFinding[] }[] {
  return CLASS_ORDER.map((cls) => ({ cls, items: findings.filter((f) => f.class === cls) })).filter(
    (g) => g.items.length > 0,
  );
}

/** The actionable findings about one booking. A deposit's reference is the
 *  résa; a settlement's is the facture, so the client number is the join that
 *  works for both. Never joins on a null on either side. */
export function findingsFor(
  findings: readonly CardFinding[],
  clientRef: string | null,
  bookingRef: string | null,
): CardFinding[] {
  return findings.filter(
    (f) =>
      isActionable(f) &&
      ((clientRef !== null && f.clientRef === clientRef) ||
        (bookingRef !== null && f.reference !== null && f.reference === bookingRef)),
  );
}

const hhmm = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })
    : null;

/** One plain sentence per finding, for the row under the booking: what money,
 *  when, and what is wrong. No tooltip: the desk must read it without hovering. */
export function findingLine(f: CardFinding): string {
  const when = hhmm(f.tappedAt) ?? hhmm(f.writtenAt);
  return `${fmtAmount(f.amountCents)} €${when ? ` à ${when}` : ''} : ${f.explanation}`;
}
